import { HeartHandshake } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

const frequencyOptions = [
  "Nikad",
  "Veoma retko",
  "Ponekad",
  "Često",
  "Veoma često",
  "Gotovo uvek",
];

const increasingScores = [0, 1, 2, 3, 4, 5];
const decreasingScores = [5, 4, 3, 2, 1, 0];

export const testEmocionalneZavisnosti: QuizConfig = {
  id: "test-emocionalne-zavisnosti",
  illustration: {
    src: "/images/quizzes/test-emocionalne-zavisnosti.webp",
    alt: "Muškarac prekida slikanje da bi proverio poruku na telefonu.",
  },
  lastModified: "2026-07-31",
  title: "Test emocionalne zavisnosti: Da li gubiš sebe u vezi?",
  shortTitle: "Test emocionalne zavisnosti",
  description:
    "Test emocionalne zavisnosti za samoprocenu: prepoznaj potrebu za potvrdom, strah od napuštanja i gubitak sebe u vezi. Nije dijagnoza.",
  longDescription:
    "Ovaj test emocionalne zavisnosti procenjuje koliko tvoj mir, odluke i osećaj vrednosti zavise od partnerove potvrde. Pitanja pokrivaju strah od napuštanja, teškoće sa granicama i očuvanje sopstvenog života. Rezultat je pokazatelj obrasca, ne dijagnoza.",
  icon: HeartHandshake,
  duration: "3-4 min",
  completions: 0,
  category: "Odnosi i komunikacija",
  featured: false,
  focusAreasWanted: [
    "relationships",
    "boundaries",
    "emotional_regulation",
    "couples_issues",
  ],
  relatedDirectorySlugs: ["kodependencija", "problemi-vezivanja", "odnosi"],
  promptExtension: `
    This is a non-diagnostic self-assessment of emotional dependence in a close relationship.
    The questions are original adaptations informed by the Emotional Dependence Questionnaire, the Interpersonal Dependency Inventory, and the Relationship Profile Test. Do not present this quiz as either validated or equivalent to those instruments.
    Reference: Lemos Hoyos and Londoño Arredondo, Emotional Dependence Questionnaire, https://editorial.ucatolica.edu.co/index.php/acta-colombiana-psicologia/article/view/405
    Reference: Hirschfeld et al., A Measure of Interpersonal Dependency, https://pubmed.ncbi.nlm.nih.gov/592089/
    Reference: Bornstein et al., Construct Validity of the Relationship Profile Test, https://doi.org/10.1177/1073191102238195

    Interpret the overall percentage using the scorer boundaries:
    - Below 40: fewer indicators of emotional dependence
    - 40 to 60: moderate indicators that deserve attention in context
    - Above 60: pronounced indicators that may be affecting autonomy, boundaries, or daily life

    These are internal, non-clinical product guidance bands. They are not validated cutoffs and must not be presented as clinical thresholds.

    Required safety framing:
    - Call the result an indicator or tendency, never a diagnosis, disorder, or proof about the relationship.
    - Do not infer that the partner is toxic, narcissistic, abusive, or responsible for the result.
    - Distinguish healthy interdependence from losing autonomy. Needing closeness and support is normal.
    - For moderate results, suggest observing triggers, restoring routines outside the relationship, and practising small boundaries.
    - For high results, warmly recommend talking with a licensed mental health professional, especially if fear, guilt, or loss of autonomy is narrowing daily life.
    - If the answers suggest control, threats, humiliation, or fear, prioritize personal safety and trusted professional support. Do not recommend couples work as the first response to possible abuse.
  `,
  questions: [
    {
      type: "radio",
      question:
        "U poslednja tri meseca, koliko često ti se raspoloženje naglo promenilo zbog partnerove poruke, tona ili ćutanja?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često tražiš potvrdu da te partner voli, čak i kada si je nedavno dobio/la?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često menjaš svoje planove da bi ostao/la dostupan/na partneru?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često ti je teško da doneseš važnu odluku bez partnerovog odobrenja?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često prećutiš svoju potrebu ili neslaganje jer se plašiš udaljavanja ili raskida?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često ti pomisao da budeš bez partnera deluje nepodnošljivo, čak i kada odnos ne zadovoljava tvoje potrebe?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često osećaš jaku krivicu kada postaviš granicu partneru?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često pratiš partnerovo raspoloženje i pokušavaš da ga popraviš, iako nije jasno da si za njega odgovoran/na?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često ostaješ u situacijama koje ti ne prijaju zato što se bojiš da ćeš izgubiti odnos?",
      options: frequencyOptions,
      scores: increasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često čuvaš svoje prijatelje, rutine i interesovanja i kada si u bliskoj vezi?",
      options: frequencyOptions,
      scores: decreasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često uspeš da umiriš sebe pre nego što od partnera zatražiš potvrdu ili objašnjenje?",
      options: frequencyOptions,
      scores: decreasingScores,
    },
    {
      type: "radio",
      question:
        "Koliko često možeš da se ne složiš sa partnerom bez straha da time ugrožavaš celu vezu?",
      options: frequencyOptions,
      scores: decreasingScores,
    },
    {
      type: "textarea",
      question:
        "Opiši kako strah od gubitka partnera utiče na tvoje odluke ili ponašanje:",
    },
  ],
  copy: {
    title: "Tvoj rezultat",
    subtitle: "Pokazatelji emocionalne zavisnosti u vezi",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Kako da sačuvaš sebe u bliskosti",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  landing: {
    summary:
      "Odgovori na 13 pitanja o potvrdi, granicama, samostalnosti i strahu od gubitka odnosa. Dobićeš orijentacioni rezultat koji može da ti pomogne da prepoznaš obrazac, bez etiketiranja sebe ili partnera.",
    highlights: [
      {
        title: "13 pitanja, bez dijagnoze",
        description:
          "Procena pokazuje tendencije u odnosu. Ne utvrđuje poremećaj i ne može da zameni razgovor sa stručnjakom.",
      },
      {
        title: "Fokus na tvoju autonomiju",
        description:
          "Pitanja razlikuju zdravu bliskost od gubitka rutina, granica i unutrašnjeg oslonca.",
      },
      {
        title: "Jasni sledeći koraci",
        description:
          "Rezultat dobija smisao kroz konkretne predloge za posmatranje okidača, male granice i stručnu podršku kada je potrebna.",
      },
    ],
    whoIsItFor:
      "Za tebe ako često proveravaš da li je odnos siguran, teško kažeš ne ili osećaš da tvoj mir previše zavisi od partnerove pažnje.",
    whatYouLearn:
      "Videćeš koliko su prisutni potreba za potvrdom, strah od napuštanja, odricanje od sopstvenih potreba i teškoće da zadržiš život van veze.",
    howItWorks:
      "Za svaku tvrdnju označi koliko često je važila za tebe tokom poslednja tri meseca. Odgovaraj prema stvarnom iskustvu, ne prema tome kako bi voleo/la da odnos izgleda.",
    nextSteps:
      "Rezultat koristi kao početak razmišljanja. Ako strah, krivica ili gubitak granica sužavaju tvoj život, razgovor sa licenciranim terapeutom može pomoći da obrazac razumeš u svom kontekstu.",
    faq: [
      {
        question: "Da li je emocionalna zavisnost dijagnoza?",
        answer:
          "Ne. Ovde se izraz koristi za obrazac u kome mir, odluke ili osećaj vrednosti previše zavise od bliske osobe. Test ne postavlja dijagnozu.",
      },
      {
        question: "Da li potreba za partnerom znači emocionalnu zavisnost?",
        answer:
          "Ne. Bliskost, podrška i međuzavisnost su normalni delovi odnosa. Važno je da li uz bliskost možeš da zadržiš svoje granice, odluke i druge izvore podrške.",
      },
      {
        question: "Na čemu su pitanja zasnovana?",
        answer:
          "Pitanja su originalna samoprocena inspirisana istraživačkim domenima emocionalne i interpersonalne zavisnosti. Ovaj kraći test nije validirana verzija instrumenata korišćenih kao stručni okvir.",
      },
      {
        question: "Šta ako dobijem visok rezultat?",
        answer:
          "Visok rezultat nije etiketa ni dokaz da je veza loša. Može ukazati da obrazac utiče na tvoju autonomiju ili svakodnevicu, pa vredi razgovarati sa licenciranim stručnjakom.",
      },
    ],
    ctaLabel: "Započni test za emocionalnu zavisnost",
  },
};
